import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Archive, Pencil, Plus, Ruler, Upload } from 'lucide-react';
import { toast } from 'sonner';

import { useAuth } from '../../context/AuthContext';
import { usersAPI } from '../../lib/api';
import { fetchAllPages } from '../../lib/fetchAllPages';
import { stationsApi } from '../../lib/stationsApi';
import { messageErreur } from '../../lib/erreurs';
import { libelle, nomLisible, optionsLibelles } from '../../lib/libelles';
import { formatLitres, formatMontant, formatNombre } from '../../lib/formats';
import { normalizeChart, parseChartCsv, tolerance } from '../../lib/stationCalc';
import { PageHeader } from '../../components/ui/page-header';
import { Aide } from '../../components/ui/aide';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { SearchableSelect } from '../../components/ui/searchable-select';
import { LoadingState } from '../../components/ui/loading-state';
import { EmptyState } from '../../components/ui/empty-state';
import { StationPicker, useDroitsStation, useStations } from '../../components/stations/StationPicker';
import { TankGauge } from '../../components/stations/TankGauge';
import { Readout } from '../../components/stations/Readout';
import StationManagementSettings from '../../components/stations/StationManagementSettings';
import PumpRegistry from '../../components/stations/PumpRegistry';

/**
 * Réglages du programme Stations (lot 029) : stations, produits, cuves et
 * barèmes, pompes et pistolets, équipe, seuils. Une station courante en haut
 * (cherchable) ; chaque onglet ne montre que ce que les droits permettent.
 */

const ONGLETS = [
  ['stations', 'Stations'],
  ['produits', 'Produits'],
  ['cuves', 'Cuves et barèmes'],
  ['pompes', 'Pompes et pistolets'],
  ['equipe', 'Équipe'],
  ['seuils', 'Seuils'],
  ['gerance', 'Gérance'],
  ['registre', 'Registre des pompes'],
];

const PAYS_STATION = ['ML', 'CI', 'SN', 'BF', 'NE', 'GN', 'MR', 'TG', 'BJ'];

const nombreOuVide = (valeur) => (valeur === '' || valeur === null || valeur === undefined ? undefined : Number(valeur));

const Champ = ({ id, label, aide, children }) => (
  <div className="grid gap-1.5">
    <label htmlFor={id} className="flex items-center gap-1 text-sm font-medium text-foreground">
      {label}
      {aide}
    </label>
    {children}
  </div>
);

const Tableau = ({ entetes, children, vide }) => (
  <div className="overflow-x-auto rounded-xl border border-border bg-card shadow-sm">
    <table className="w-full border-collapse text-sm">
      <thead>
        <tr>
          {entetes.map(([texte, droite]) => (
            <th
              key={texte || 'actions'}
              className={`whitespace-nowrap border-b border-border px-3 py-2 text-xs font-semibold text-muted-foreground ${droite ? 'text-right' : 'text-left'}`}
            >
              {texte}
            </th>
          ))}
        </tr>
      </thead>
      <tbody className="[&_td]:border-b [&_td]:border-border/60 [&_td]:px-3 [&_td]:py-2 [&_tr:last-child_td]:border-0">
        {children}
      </tbody>
    </table>
    {vide}
  </div>
);

const BoutonIcone = ({ label, onClick, children, disabled }) => (
  <Button type="button" variant="ghost" size="icon" title={label} aria-label={label} onClick={onClick} disabled={disabled}>
    {children}
  </Button>
);

// --- Stations ----------------------------------------------------------------------------

const STATION_VIDE = {
  name: '',
  code: '',
  city: '',
  address: '',
  phone: '',
  operating_mode: 'OWNED',
  country: 'ML',
  timezone: 'Africa/Bamako',
  manager_user_id: '',
  manager_name: '',
};

const StationDialog = ({ open, onOpenChange, station, utilisateurs, peutChoisirGerant, onSaved }) => {
  const [form, setForm] = useState(STATION_VIDE);
  const [enCours, setEnCours] = useState(false);
  useEffect(() => {
    if (!open) return;
    setForm(
      station
        ? {
            ...STATION_VIDE,
            ...Object.fromEntries(Object.keys(STATION_VIDE).map((cle) => [cle, station[cle] ?? STATION_VIDE[cle]])),
          }
        : STATION_VIDE,
    );
  }, [open, station]);
  const changer = (cle) => (event) => setForm((avant) => ({ ...avant, [cle]: event?.target ? event.target.value : event }));
  const optionsUtilisateurs = useMemo(
    () =>
      (utilisateurs || []).map((utilisateur) => ({
        value: utilisateur.id,
        label: nomLisible(utilisateur.full_name, utilisateur.email),
        description: utilisateur.email || undefined,
      })),
    [utilisateurs],
  );

  const enregistrer = async (event) => {
    event.preventDefault();
    const corps = {
      name: form.name.trim(),
      code: form.code.trim().toUpperCase(),
      city: form.city.trim() || undefined,
      address: form.address.trim() || undefined,
      phone: form.phone.trim() || undefined,
      operating_mode: form.operating_mode,
      country: form.country,
      timezone: form.timezone,
      manager_user_id: peutChoisirGerant ? form.manager_user_id || undefined : undefined,
      manager_name: !peutChoisirGerant || !form.manager_user_id ? form.manager_name.trim() || undefined : undefined,
    };
    setEnCours(true);
    try {
      const enregistree = station ? await stationsApi.stations.update(station.id, corps) : await stationsApi.stations.create(corps);
      toast.success(station ? 'Station modifiée' : 'Station créée');
      onSaved(enregistree);
      onOpenChange(false);
    } catch (erreur) {
      toast.error(messageErreur(erreur, 'Impossible d’enregistrer la station'));
    } finally {
      setEnCours(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{station ? 'Modifier la station' : 'Nouvelle station'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={enregistrer} className="grid gap-4 sm:grid-cols-2">
          <Champ id="station-nom" label="Nom">
            <Input id="station-nom" value={form.name} onChange={changer('name')} required maxLength={255} />
          </Champ>
          <Champ id="station-code" label="Code">
            <Input id="station-code" value={form.code} onChange={changer('code')} required maxLength={50} />
          </Champ>
          <Champ id="station-ville" label="Ville">
            <Input id="station-ville" value={form.city} onChange={changer('city')} maxLength={100} />
          </Champ>
          <Champ id="station-telephone" label="Téléphone">
            <Input id="station-telephone" value={form.phone} onChange={changer('phone')} maxLength={50} />
          </Champ>
          <div className="sm:col-span-2">
            <Champ id="station-adresse" label="Adresse">
              <Input id="station-adresse" value={form.address} onChange={changer('address')} maxLength={255} />
            </Champ>
          </div>
          <Champ id="station-mode" label="Mode d’exploitation">
            <SearchableSelect
              id="station-mode"
              value={form.operating_mode}
              onValueChange={changer('operating_mode')}
              options={optionsLibelles('station_mode')}
              aria-label="Mode d’exploitation"
            />
          </Champ>
          <Champ id="station-pays" label="Pays">
            <SearchableSelect
              id="station-pays"
              value={form.country}
              onValueChange={changer('country')}
              options={optionsLibelles('pays', PAYS_STATION)}
              aria-label="Pays"
            />
          </Champ>
          <Champ id="station-fuseau" label="Heure de la journée">
            <SearchableSelect
              id="station-fuseau"
              value={form.timezone}
              onValueChange={changer('timezone')}
              options={optionsLibelles('station_fuseau')}
              aria-label="Heure de la journée"
            />
          </Champ>
          {peutChoisirGerant ? (
            <Champ id="station-gerant" label="Gérant">
              <SearchableSelect
                id="station-gerant"
                value={form.manager_user_id}
                onValueChange={changer('manager_user_id')}
                options={optionsUtilisateurs}
                aria-label="Gérant"
                placeholder="Choisir le compte du gérant"
                searchPlaceholder="Rechercher un compte…"
                emptyMessage="Aucun compte"
              />
            </Champ>
          ) : (
            <Champ id="station-gerant-nom" label="Gérant">
              <Input id="station-gerant-nom" value={form.manager_name} onChange={changer('manager_name')} maxLength={255} />
            </Champ>
          )}
          <DialogFooter className="sm:col-span-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Annuler
            </Button>
            <Button type="submit" disabled={enCours}>
              Enregistrer
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const OngletStations = ({ stations, chargement, peutEcrire, onEditer, onArchiver }) => {
  if (chargement) return <LoadingState shape="table" />;
  if (!stations.length) return <EmptyState variant="empty" title="Aucune station pour le moment" className="border-0" />;
  return (
    <Tableau
      entetes={[['Station'], ['Ville'], ['Mode d’exploitation'], ['Gérant'], ['Heure'], ['État'], ['']]}
    >
      {stations.map((station) => (
        <tr key={station.id}>
          <td>
            <div className="font-medium text-foreground">{nomLisible(station.name, station.code)}</div>
            <div className="font-mono text-xs text-muted-foreground">{station.code}</div>
          </td>
          <td>{station.city || '—'}</td>
          <td>{libelle('station_mode', station.operating_mode || 'OWNED')}</td>
          <td>{station.manager_name || '—'}</td>
          <td>{libelle('station_fuseau', station.timezone || 'Africa/Bamako')}</td>
          <td>
            <Badge variant={station.status === 'ACTIVE' ? 'success' : 'muted'}>{libelle('stationStatus', station.status)}</Badge>
          </td>
          <td className="whitespace-nowrap text-right">
            {peutEcrire && (
              <>
                <BoutonIcone label={`Modifier ${station.name}`} onClick={() => onEditer(station)}>
                  <Pencil className="h-4 w-4" />
                </BoutonIcone>
                <BoutonIcone label={`Archiver ${station.name}`} onClick={() => onArchiver(station)}>
                  <Archive className="h-4 w-4 text-destructive" />
                </BoutonIcone>
              </>
            )}
          </td>
        </tr>
      ))}
    </Tableau>
  );
};

// --- Produits ------------------------------------------------------------------------------

const PRODUIT_VIDE = { code: '', name: '', family: 'FUEL', unit: 'L', expansion_coeff: '', is_active: true };

const ProduitDialog = ({ open, onOpenChange, produit, onSaved }) => {
  const [form, setForm] = useState(PRODUIT_VIDE);
  const [enCours, setEnCours] = useState(false);
  useEffect(() => {
    if (!open) return;
    setForm(
      produit
        ? {
            code: produit.code,
            name: produit.name,
            family: produit.family,
            unit: produit.unit,
            expansion_coeff: produit.expansion_coeff ?? '',
            is_active: produit.is_active !== false,
          }
        : PRODUIT_VIDE,
    );
  }, [open, produit]);
  const changer = (cle) => (event) => setForm((avant) => ({ ...avant, [cle]: event?.target ? event.target.value : event }));

  const enregistrer = async (event) => {
    event.preventDefault();
    const corps = {
      name: form.name.trim(),
      family: form.family,
      unit: form.unit,
      expansion_coeff: nombreOuVide(form.expansion_coeff),
    };
    setEnCours(true);
    try {
      const enregistre = produit
        ? await stationsApi.products.update(produit.id, { ...corps, is_active: form.is_active })
        : await stationsApi.products.create({ ...corps, code: form.code.trim().toUpperCase() });
      toast.success(produit ? 'Produit modifié' : 'Produit créé');
      onSaved(enregistre);
      onOpenChange(false);
    } catch (erreur) {
      toast.error(messageErreur(erreur, 'Impossible d’enregistrer le produit'));
    } finally {
      setEnCours(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{produit ? 'Modifier le produit' : 'Nouveau produit'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={enregistrer} className="grid gap-4 sm:grid-cols-2">
          <Champ id="produit-nom" label="Nom">
            <Input id="produit-nom" value={form.name} onChange={changer('name')} required maxLength={100} />
          </Champ>
          <Champ id="produit-code" label="Code">
            <Input
              id="produit-code"
              value={form.code}
              onChange={changer('code')}
              required
              maxLength={30}
              disabled={Boolean(produit)}
            />
          </Champ>
          <Champ id="produit-famille" label="Famille">
            <SearchableSelect
              id="produit-famille"
              value={form.family}
              onValueChange={changer('family')}
              options={optionsLibelles('station_famille')}
              aria-label="Famille"
            />
          </Champ>
          <Champ id="produit-unite" label="Unité">
            <SearchableSelect
              id="produit-unite"
              value={form.unit}
              onValueChange={changer('unit')}
              options={optionsLibelles('station_unite')}
              aria-label="Unité"
            />
          </Champ>
          <Champ
            id="produit-coeff"
            label="Coefficient de dilatation"
            aide={
              <Aide titre="Coefficient de dilatation">
                Sert à ramener les litres jaugés à 15 °C. Gasoil 0,00083 ; super 0,00120 ; pétrole lampant 0,00090.
              </Aide>
            }
          >
            <Input
              id="produit-coeff"
              type="number"
              inputMode="decimal"
              step="0.00001"
              min="0"
              max="0.01"
              value={form.expansion_coeff}
              onChange={changer('expansion_coeff')}
            />
          </Champ>
          {produit && (
            <label className="flex items-center justify-between gap-3 self-end rounded-lg border border-border p-3 text-sm">
              <span>Vendu</span>
              <Switch checked={form.is_active} onCheckedChange={changer('is_active')} aria-label="Produit vendu" />
            </label>
          )}
          <DialogFooter className="sm:col-span-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Annuler
            </Button>
            <Button type="submit" disabled={enCours}>
              Enregistrer
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const OngletProduits = ({ produits, peutEcrire, onEditer }) => {
  if (!produits.length) return <EmptyState variant="empty" title="Aucun produit pour le moment" className="border-0" />;
  return (
    <Tableau entetes={[['Produit'], ['Famille'], ['Unité'], ['Coefficient de dilatation', true], ['État'], ['']]}>
      {produits.map((produit) => (
        <tr key={produit.id}>
          <td className="font-medium text-foreground">{produit.name}</td>
          <td>{libelle('station_famille', produit.family)}</td>
          <td>{libelle('station_unite', produit.unit)}</td>
          <td className="text-right tabular-nums">
            {produit.expansion_coeff === null || produit.expansion_coeff === undefined ? '—' : formatNombre(produit.expansion_coeff, 5)}
          </td>
          <td>
            <Badge variant={produit.is_active ? 'success' : 'muted'}>{produit.is_active ? 'Vendu' : 'Retiré'}</Badge>
          </td>
          <td className="text-right">
            {peutEcrire && (
              <BoutonIcone label={`Modifier ${produit.name}`} onClick={() => onEditer(produit)}>
                <Pencil className="h-4 w-4" />
              </BoutonIcone>
            )}
          </td>
        </tr>
      ))}
    </Tableau>
  );
};

// --- Cuves et barèmes ---------------------------------------------------------------------

const CUVE_VIDE = { name: '', product_id: '', capacity_liters: '', min_threshold_liters: '', initial_volume_liters: '' };

const CuveDialog = ({ open, onOpenChange, stationId, cuve, produits, onSaved }) => {
  const [form, setForm] = useState(CUVE_VIDE);
  const [enCours, setEnCours] = useState(false);
  useEffect(() => {
    if (!open) return;
    setForm(
      cuve
        ? {
            name: cuve.name || '',
            product_id: cuve.product_id || '',
            capacity_liters: cuve.capacity_liters ?? '',
            min_threshold_liters: cuve.min_threshold_liters ?? '',
            initial_volume_liters: '',
          }
        : CUVE_VIDE,
    );
  }, [open, cuve]);
  const changer = (cle) => (event) => setForm((avant) => ({ ...avant, [cle]: event?.target ? event.target.value : event }));
  const optionsProduits = useMemo(
    () =>
      produits
        .filter((produit) => produit.unit === 'L' && (produit.is_active || produit.id === cuve?.product_id))
        .map((produit) => ({ value: produit.id, label: produit.name, description: libelle('station_famille', produit.family) })),
    [produits, cuve],
  );

  const enregistrer = async (event) => {
    event.preventDefault();
    if (!form.product_id) {
      toast.error('Choisissez le produit de la cuve.');
      return;
    }
    const corps = {
      name: form.name.trim(),
      product_id: form.product_id,
      capacity_liters: nombreOuVide(form.capacity_liters),
      min_threshold_liters: nombreOuVide(form.min_threshold_liters) ?? 0,
    };
    setEnCours(true);
    try {
      const enregistree = cuve
        ? await stationsApi.tanks.update(stationId, cuve.id, corps)
        : await stationsApi.tanks.create(stationId, { ...corps, initial_volume_liters: nombreOuVide(form.initial_volume_liters) ?? 0 });
      toast.success(cuve ? 'Cuve modifiée' : 'Cuve créée');
      onSaved(enregistree);
      onOpenChange(false);
    } catch (erreur) {
      toast.error(messageErreur(erreur, 'Impossible d’enregistrer la cuve'));
    } finally {
      setEnCours(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{cuve ? 'Modifier la cuve' : 'Nouvelle cuve'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={enregistrer} className="grid gap-4 sm:grid-cols-2">
          <Champ id="cuve-nom" label="Nom">
            <Input id="cuve-nom" value={form.name} onChange={changer('name')} required maxLength={100} />
          </Champ>
          <Champ id="cuve-produit" label="Produit">
            <SearchableSelect
              id="cuve-produit"
              value={form.product_id}
              onValueChange={changer('product_id')}
              options={optionsProduits}
              aria-label="Produit"
              placeholder="Choisir le produit"
            />
          </Champ>
          <Champ id="cuve-capacite" label="Capacité (L)">
            <Input id="cuve-capacite" type="number" inputMode="decimal" min="1" step="1" value={form.capacity_liters} onChange={changer('capacity_liters')} required />
          </Champ>
          <Champ id="cuve-seuil" label="Seuil bas (L)">
            <Input id="cuve-seuil" type="number" inputMode="decimal" min="0" step="1" value={form.min_threshold_liters} onChange={changer('min_threshold_liters')} />
          </Champ>
          {!cuve && (
            <Champ
              id="cuve-stock"
              label="Stock de départ (L)"
              aide={
                <Aide titre="Stock de départ">
                  Le stock de départ entre dans le grand livre comme un ajustement. Ensuite, le volume de la cuve ne
                  bouge qu’avec les réceptions, les ventes et les ajustements motivés.
                </Aide>
              }
            >
              <Input id="cuve-stock" type="number" inputMode="decimal" min="0" step="1" value={form.initial_volume_liters} onChange={changer('initial_volume_liters')} />
            </Champ>
          )}
          <DialogFooter className="sm:col-span-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Annuler
            </Button>
            <Button type="submit" disabled={enCours}>
              Enregistrer
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const BaremeDialog = ({ open, onOpenChange, cuve, onSaved }) => {
  const [texte, setTexte] = useState('');
  const [nom, setNom] = useState('');
  const [enCours, setEnCours] = useState(false);
  useEffect(() => {
    if (!open) return;
    setNom(cuve?.chart_label || '');
    setTexte((cuve?.chart_points || []).map(([hauteur, litres]) => `${hauteur};${litres}`).join('\n'));
  }, [open, cuve]);

  const lecture = useMemo(() => {
    const points = parseChartCsv(texte);
    if (!points.length) return { points: [], erreur: null };
    try {
      return { points: normalizeChart(points), erreur: null };
    } catch (erreur) {
      return { points: [], erreur: erreur.message };
    }
  }, [texte]);

  const importer = (event) => {
    const fichier = event.target.files?.[0];
    if (!fichier) return;
    const lecteur = new FileReader();
    lecteur.onload = () => setTexte(String(lecteur.result || ''));
    lecteur.readAsText(fichier);
    event.target.value = '';
  };

  const enregistrer = async (points) => {
    setEnCours(true);
    try {
      const enregistree = await stationsApi.tanks.setChart(cuve.id, points, nom.trim() || null);
      toast.success(points.length ? 'Barème enregistré' : 'Barème retiré');
      onSaved(enregistree);
      onOpenChange(false);
    } catch (erreur) {
      toast.error(messageErreur(erreur, 'Impossible d’enregistrer le barème'));
    } finally {
      setEnCours(false);
    }
  };

  const premier = lecture.points[0];
  const dernier = lecture.points[lecture.points.length - 1];
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-1">
            Barème de {cuve?.name || 'la cuve'}
            <Aide titre="Importer un barème">
              Une ligne par mesure : la hauteur en centimètres, un point-virgule, puis les litres (exemple : 94,7;11289).
              Les lignes d’en-tête sont ignorées. Un fichier CSV enregistré depuis Excel convient.
            </Aide>
          </DialogTitle>
        </DialogHeader>
        <div className="grid gap-4">
          <Champ id="bareme-nom" label="Nom du barème">
            <Input id="bareme-nom" value={nom} onChange={(event) => setNom(event.target.value)} maxLength={80} />
          </Champ>
          <Champ id="bareme-lignes" label="Hauteur;litres">
            <textarea
              id="bareme-lignes"
              rows={8}
              value={texte}
              onChange={(event) => setTexte(event.target.value)}
              className="w-full rounded-md border border-input bg-card px-3 py-2 font-mono text-sm shadow-sm focus-visible:border-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/30"
              aria-invalid={Boolean(lecture.erreur)}
            />
          </Champ>
          <label className="inline-flex w-fit cursor-pointer items-center gap-2 rounded-md border border-border px-3 py-2 text-sm font-medium hover:bg-accent">
            <Upload className="h-4 w-4" aria-hidden="true" />
            Importer un fichier
            <input type="file" accept=".csv,.txt,text/csv,text/plain" className="sr-only" onChange={importer} />
          </label>
          {lecture.erreur ? (
            <p className="text-sm font-medium text-destructive" role="alert">{lecture.erreur}</p>
          ) : lecture.points.length > 0 ? (
            <dl className="grid grid-cols-3 gap-2 rounded-lg border border-border p-3 text-sm">
              <div>
                <dt className="text-xs text-muted-foreground">Lignes</dt>
                <dd className="m-0 font-semibold tabular-nums">{formatNombre(lecture.points.length)}</dd>
              </div>
              <div>
                <dt className="text-xs text-muted-foreground">Bas</dt>
                <dd className="m-0 tabular-nums">{formatNombre(premier[0], 1)} cm · {formatLitres(premier[1])}</dd>
              </div>
              <div>
                <dt className="text-xs text-muted-foreground">Haut</dt>
                <dd className="m-0 tabular-nums">{formatNombre(dernier[0], 1)} cm · {formatLitres(dernier[1])}</dd>
              </div>
            </dl>
          ) : null}
          <DialogFooter>
            {cuve?.chart_points?.length > 0 && (
              <Button type="button" variant="outline" onClick={() => enregistrer([])} disabled={enCours}>
                Retirer le barème
              </Button>
            )}
            <Button type="button" onClick={() => enregistrer(lecture.points)} disabled={enCours || !lecture.points.length}>
              Enregistrer
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const OngletCuves = ({ cuves, produits, peutEcrire, onEditer, onBareme }) => {
  const nomProduit = (cuve) =>
    produits.find((produit) => produit.id === cuve.product_id)?.name || libelle('fuelProduct', cuve.product, 'Produit à préciser');
  const codeProduit = (cuve) => produits.find((produit) => produit.id === cuve.product_id)?.code;
  if (!cuves.length) return <EmptyState variant="empty" title="Aucune cuve dans cette station" className="border-0" />;
  return (
    <ul className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
      {cuves.map((cuve) => (
        <li key={cuve.id} className="flex flex-col gap-3 rounded-xl border border-border bg-card p-4 shadow-sm">
          <TankGauge
            name={cuve.name}
            productName={nomProduit(cuve)}
            volume={cuve.current_volume_liters}
            capacity={cuve.capacity_liters}
            threshold={cuve.min_threshold_liters}
            tone={codeProduit(cuve) === 'SUPER' ? 'success' : 'primary'}
          />
          <div className="flex flex-wrap items-center justify-between gap-2 border-t border-border pt-3 text-sm">
            <span className="inline-flex items-center gap-1.5 text-muted-foreground">
              <Ruler className="h-4 w-4" aria-hidden="true" />
              {cuve.chart_points?.length
                ? `${cuve.chart_label || 'Barème'} · ${formatNombre(cuve.chart_points.length)} lignes`
                : 'Sans barème'}
            </span>
            {!cuve.is_active && <Badge variant="muted">Hors service</Badge>}
            {peutEcrire && (
              <span className="flex gap-1">
                <Button type="button" variant="outline" size="sm" onClick={() => onBareme(cuve)}>
                  Barème
                </Button>
                <BoutonIcone label={`Modifier ${cuve.name}`} onClick={() => onEditer(cuve)}>
                  <Pencil className="h-4 w-4" />
                </BoutonIcone>
              </span>
            )}
          </div>
        </li>
      ))}
    </ul>
  );
};

// --- Pompes et pistolets ------------------------------------------------------------------

const PompeDialog = ({ open, onOpenChange, stationId, pompe, cuves, onSaved }) => {
  const [form, setForm] = useState({ name: '', tank_id: '', meter_reading_liters: '', is_active: true });
  const [enCours, setEnCours] = useState(false);
  useEffect(() => {
    if (!open) return;
    setForm(
      pompe
        ? { name: pompe.name || '', tank_id: '', meter_reading_liters: '', is_active: pompe.is_active !== false }
        : { name: '', tank_id: '', meter_reading_liters: '', is_active: true },
    );
  }, [open, pompe]);
  const changer = (cle) => (event) => setForm((avant) => ({ ...avant, [cle]: event?.target ? event.target.value : event }));
  const optionsCuves = useMemo(
    () => cuves.filter((cuve) => cuve.is_active).map((cuve) => ({ value: cuve.id, label: cuve.name })),
    [cuves],
  );

  const enregistrer = async (event) => {
    event.preventDefault();
    setEnCours(true);
    try {
      const enregistree = pompe
        ? await stationsApi.pumps.update(stationId, pompe.id, { name: form.name.trim(), is_active: form.is_active })
        : await stationsApi.pumps.create(stationId, {
            name: form.name.trim(),
            tank_id: form.tank_id || undefined,
            meter_reading_liters: nombreOuVide(form.meter_reading_liters) ?? 0,
          });
      toast.success(pompe ? 'Pompe modifiée' : 'Pompe créée');
      onSaved(enregistree);
      onOpenChange(false);
    } catch (erreur) {
      toast.error(messageErreur(erreur, 'Impossible d’enregistrer la pompe'));
    } finally {
      setEnCours(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{pompe ? 'Modifier la pompe' : 'Nouvelle pompe'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={enregistrer} className="grid gap-4 sm:grid-cols-2">
          <Champ id="pompe-nom" label="Nom">
            <Input id="pompe-nom" value={form.name} onChange={changer('name')} required maxLength={100} />
          </Champ>
          {pompe ? (
            <label className="flex items-center justify-between gap-3 self-end rounded-lg border border-border p-3 text-sm">
              <span>En service</span>
              <Switch checked={form.is_active} onCheckedChange={changer('is_active')} aria-label="Pompe en service" />
            </label>
          ) : (
            <>
              <Champ id="pompe-cuve" label="Cuve du premier pistolet">
                <SearchableSelect
                  id="pompe-cuve"
                  value={form.tank_id}
                  onValueChange={changer('tank_id')}
                  options={optionsCuves}
                  aria-label="Cuve du premier pistolet"
                  placeholder="Choisir la cuve"
                />
              </Champ>
              <Champ id="pompe-index" label="Index du compteur">
                <Input id="pompe-index" type="number" inputMode="decimal" min="0" step="0.01" value={form.meter_reading_liters} onChange={changer('meter_reading_liters')} />
              </Champ>
            </>
          )}
          <DialogFooter className="sm:col-span-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Annuler
            </Button>
            <Button type="submit" disabled={enCours}>
              Enregistrer
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const PistoletDialog = ({ open, onOpenChange, stationId, pistolet, pompeId, pompes, cuves, onSaved }) => {
  const [form, setForm] = useState({});
  const [enCours, setEnCours] = useState(false);
  useEffect(() => {
    if (!open) return;
    setForm(
      pistolet
        ? {
            pump_id: pistolet.pump_id,
            tank_id: pistolet.tank_id,
            number: pistolet.number ?? '',
            label: pistolet.label || '',
            is_active: pistolet.is_active !== false,
          }
        : { pump_id: pompeId || '', tank_id: '', number: '', label: '', initial_index: '', is_active: true },
    );
  }, [open, pistolet, pompeId]);
  const changer = (cle) => (event) => setForm((avant) => ({ ...avant, [cle]: event?.target ? event.target.value : event }));
  const optionsPompes = useMemo(() => pompes.map((pompe) => ({ value: pompe.id, label: pompe.name })), [pompes]);
  const optionsCuves = useMemo(
    () => cuves.filter((cuve) => cuve.is_active).map((cuve) => ({ value: cuve.id, label: cuve.name })),
    [cuves],
  );

  const enregistrer = async (event) => {
    event.preventDefault();
    if (!form.pump_id || !form.tank_id) {
      toast.error('Choisissez la pompe et la cuve du pistolet.');
      return;
    }
    const corps = {
      pump_id: form.pump_id,
      tank_id: form.tank_id,
      number: nombreOuVide(form.number),
      label: form.label.trim() || undefined,
    };
    setEnCours(true);
    try {
      const enregistre = pistolet
        ? await stationsApi.nozzles.update(stationId, pistolet.id, { ...corps, is_active: form.is_active })
        : await stationsApi.nozzles.create(stationId, { ...corps, initial_index: nombreOuVide(form.initial_index) ?? 0 });
      toast.success(pistolet ? 'Pistolet modifié' : 'Pistolet ajouté');
      onSaved(enregistre);
      onOpenChange(false);
    } catch (erreur) {
      toast.error(messageErreur(erreur, 'Impossible d’enregistrer le pistolet'));
    } finally {
      setEnCours(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-1">
            {pistolet ? 'Modifier le pistolet' : 'Nouveau pistolet'}
            <Aide titre="Pistolet">
              Le produit du pistolet est celui de sa cuve. L’index ne se règle qu’à la pose : ensuite, il avance avec
              les relevés des postes.
            </Aide>
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={enregistrer} className="grid gap-4 sm:grid-cols-2">
          <Champ id="pistolet-pompe" label="Pompe">
            <SearchableSelect id="pistolet-pompe" value={form.pump_id} onValueChange={changer('pump_id')} options={optionsPompes} aria-label="Pompe" placeholder="Choisir la pompe" />
          </Champ>
          <Champ id="pistolet-cuve" label="Cuve">
            <SearchableSelect id="pistolet-cuve" value={form.tank_id} onValueChange={changer('tank_id')} options={optionsCuves} aria-label="Cuve" placeholder="Choisir la cuve" />
          </Champ>
          <Champ id="pistolet-numero" label="Numéro">
            <Input id="pistolet-numero" type="number" inputMode="numeric" min="1" max="999" step="1" value={form.number ?? ''} onChange={changer('number')} />
          </Champ>
          <Champ id="pistolet-nom" label="Nom">
            <Input id="pistolet-nom" value={form.label ?? ''} onChange={changer('label')} maxLength={50} />
          </Champ>
          {pistolet ? (
            <label className="flex items-center justify-between gap-3 rounded-lg border border-border p-3 text-sm">
              <span>En service</span>
              <Switch checked={Boolean(form.is_active)} onCheckedChange={changer('is_active')} aria-label="Pistolet en service" />
            </label>
          ) : (
            <Champ id="pistolet-index" label="Index du compteur">
              <Input id="pistolet-index" type="number" inputMode="decimal" min="0" step="0.01" value={form.initial_index ?? ''} onChange={changer('initial_index')} />
            </Champ>
          )}
          <DialogFooter className="sm:col-span-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Annuler
            </Button>
            <Button type="submit" disabled={enCours}>
              Enregistrer
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const OngletPompes = ({ pompes, pistolets, cuves, produits, peutEcrire, onEditerPompe, onAjouterPistolet, onEditerPistolet }) => {
  if (!pompes.length) return <EmptyState variant="empty" title="Aucune pompe dans cette station" className="border-0" />;
  const nomCuve = (id) => cuves.find((cuve) => cuve.id === id)?.name || '—';
  const nomProduit = (id) => produits.find((produit) => produit.id === id)?.name || '—';
  return (
    <div className="grid gap-4">
      {pompes.map((pompe) => {
        const siens = pistolets.filter((pistolet) => pistolet.pump_id === pompe.id);
        return (
          <section key={pompe.id} className="rounded-xl border border-border bg-card shadow-sm">
            <header className="flex flex-wrap items-center justify-between gap-2 px-4 pb-2 pt-3">
              <h2 className="flex items-center gap-2 text-base font-semibold">
                {pompe.name}
                {pompe.is_active === false && <Badge variant="muted">Hors service</Badge>}
              </h2>
              {peutEcrire && (
                <span className="flex gap-1">
                  <Button type="button" variant="outline" size="sm" onClick={() => onAjouterPistolet(pompe)}>
                    <Plus className="h-4 w-4" aria-hidden="true" />
                    Pistolet
                  </Button>
                  <BoutonIcone label={`Modifier ${pompe.name}`} onClick={() => onEditerPompe(pompe)}>
                    <Pencil className="h-4 w-4" />
                  </BoutonIcone>
                </span>
              )}
            </header>
            <div className="overflow-x-auto px-1 pb-2">
              <table className="w-full border-collapse text-sm [&_td]:px-3 [&_td]:py-2 [&_th]:px-3 [&_th]:py-1.5">
                <thead>
                  <tr className="text-xs text-muted-foreground">
                    <th className="text-left font-semibold">Pistolet</th>
                    <th className="text-left font-semibold">Cuve</th>
                    <th className="text-left font-semibold">Produit</th>
                    <th className="text-right font-semibold">Index</th>
                    <th className="text-left font-semibold">État</th>
                    <th aria-label="Actions" />
                  </tr>
                </thead>
                <tbody>
                  {siens.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="text-muted-foreground">Aucun pistolet</td>
                    </tr>
                  ) : (
                    siens.map((pistolet) => (
                      <tr key={pistolet.id} className="border-t border-border/60">
                        <td className="whitespace-nowrap font-medium">
                          Pistolet {pistolet.number}
                          {pistolet.label ? <span className="text-muted-foreground"> · {pistolet.label}</span> : null}
                        </td>
                        <td>{nomCuve(pistolet.tank_id)}</td>
                        <td>{nomProduit(pistolet.product_id)}</td>
                        <td className="text-right">
                          <Readout value={pistolet.current_index} decimals={1} label={`Index du pistolet ${pistolet.number}`} />
                        </td>
                        <td>
                          <Badge variant={pistolet.is_active ? 'success' : 'muted'}>{pistolet.is_active ? 'En service' : 'Hors service'}</Badge>
                        </td>
                        <td className="text-right">
                          {peutEcrire && (
                            <BoutonIcone label={`Modifier le pistolet ${pistolet.number}`} onClick={() => onEditerPistolet(pistolet)}>
                              <Pencil className="h-4 w-4" />
                            </BoutonIcone>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </section>
        );
      })}
    </div>
  );
};

// --- Équipe ----------------------------------------------------------------------------------

const PompisteDialog = ({ open, onOpenChange, stationId, onSaved }) => {
  const [form, setForm] = useState({ name: '', phone: '' });
  const [enCours, setEnCours] = useState(false);
  useEffect(() => {
    if (open) setForm({ name: '', phone: '' });
  }, [open]);
  const enregistrer = async (event) => {
    event.preventDefault();
    setEnCours(true);
    try {
      const cree = await stationsApi.attendants.create(stationId, { name: form.name.trim(), phone: form.phone.trim() || undefined });
      toast.success('Pompiste ajouté');
      onSaved(cree);
      onOpenChange(false);
    } catch (erreur) {
      toast.error(messageErreur(erreur, 'Impossible d’ajouter le pompiste'));
    } finally {
      setEnCours(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Nouveau pompiste</DialogTitle>
        </DialogHeader>
        <form onSubmit={enregistrer} className="grid gap-4">
          <Champ id="pompiste-nom" label="Nom">
            <Input id="pompiste-nom" value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} required maxLength={255} />
          </Champ>
          <Champ id="pompiste-telephone" label="Téléphone">
            <Input id="pompiste-telephone" value={form.phone} onChange={(event) => setForm({ ...form, phone: event.target.value })} maxLength={50} />
          </Champ>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Annuler
            </Button>
            <Button type="submit" disabled={enCours}>
              Enregistrer
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const OngletEquipe = ({ station, pompistes, peutEcrireStation, peutEcrireEquipe, onModifierGerant, onBasculer }) => (
  <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,2fr)]">
    <section className="rounded-xl border border-border bg-card p-4 shadow-sm">
      <h2 className="text-base font-semibold">Gérant</h2>
      <p className="mt-2 text-lg font-medium">{station?.manager_name || '—'}</p>
      <p className="text-sm text-muted-foreground">{libelle('station_mode', station?.operating_mode || 'OWNED')}</p>
      {peutEcrireStation && (
        <Button type="button" variant="outline" size="sm" className="mt-3" onClick={onModifierGerant}>
          Changer le gérant
        </Button>
      )}
    </section>
    <Tableau entetes={[['Pompiste'], ['Téléphone'], ['En service', true]]}>
      {pompistes.length === 0 ? (
        <tr>
          <td colSpan={3} className="text-muted-foreground">Aucun pompiste</td>
        </tr>
      ) : (
        pompistes.map((pompiste) => (
          <tr key={pompiste.id}>
            <td className="font-medium">{pompiste.name}</td>
            <td>{pompiste.phone || '—'}</td>
            <td className="text-right">
              <Switch
                checked={pompiste.is_active !== false}
                disabled={!peutEcrireEquipe}
                onCheckedChange={(valeur) => onBasculer(pompiste, valeur)}
                aria-label={`${pompiste.name} en service`}
              />
            </td>
          </tr>
        ))
      )}
    </Tableau>
  </div>
);

// --- Seuils ------------------------------------------------------------------------------------

const OngletSeuils = ({ stationId, reglages, peutEcrire, onSaved }) => {
  const [form, setForm] = useState({ variance_percent: '', variance_liters: '', cash_threshold: '', head_office_validation: false });
  const [enCours, setEnCours] = useState(false);
  useEffect(() => {
    if (!reglages) return;
    setForm({
      variance_percent: reglages.variance_percent ?? '',
      variance_liters: reglages.variance_liters ?? '',
      cash_threshold: reglages.cash_threshold ?? '',
      head_office_validation: Boolean(reglages.head_office_validation),
    });
  }, [reglages]);
  const changer = (cle) => (event) => setForm((avant) => ({ ...avant, [cle]: event?.target ? event.target.value : event }));
  let exemple = null;
  try {
    exemple = tolerance(15000, form.variance_percent === '' ? undefined : form.variance_percent, form.variance_liters === '' ? undefined : form.variance_liters);
  } catch {
    exemple = null;
  }

  const enregistrer = async (event) => {
    event.preventDefault();
    setEnCours(true);
    try {
      const resultat = await stationsApi.settings.update(stationId, {
        variance_percent: nombreOuVide(form.variance_percent),
        variance_liters: nombreOuVide(form.variance_liters),
        cash_threshold: nombreOuVide(form.cash_threshold),
        head_office_validation: Boolean(form.head_office_validation),
      });
      toast.success('Seuils enregistrés');
      onSaved(resultat?.settings);
    } catch (erreur) {
      toast.error(messageErreur(erreur, 'Impossible d’enregistrer les seuils'));
    } finally {
      setEnCours(false);
    }
  };

  return (
    <form onSubmit={enregistrer} className="grid max-w-3xl gap-4 rounded-xl border border-border bg-card p-4 shadow-sm sm:grid-cols-3">
      <Champ
        id="seuil-pourcent"
        label="Écart de cuve toléré (%)"
        aide={
          <Aide titre="Écart de cuve toléré">
            Le plus grand des deux : le pourcentage des litres vendus, ou les litres minimum.
            {exemple !== null ? ` Sur 15 000 L vendus : ${formatLitres(exemple, 1)} tolérés.` : ''}
          </Aide>
        }
      >
        <Input id="seuil-pourcent" type="number" inputMode="decimal" min="0" max="10" step="0.01" value={form.variance_percent} onChange={changer('variance_percent')} disabled={!peutEcrire} />
      </Champ>
      <Champ id="seuil-litres" label="Écart de cuve minimum toléré (L)">
        <Input id="seuil-litres" type="number" inputMode="decimal" min="0" step="1" value={form.variance_liters} onChange={changer('variance_liters')} disabled={!peutEcrire} />
      </Champ>
      <Champ
        id="seuil-caisse"
        label="Écart de caisse toléré (FCFA)"
        aide={<Aide titre="Écart de caisse toléré">Au-delà, la clôture du poste exige une justification.</Aide>}
      >
        <Input id="seuil-caisse" type="number" inputMode="numeric" min="0" step="1" value={form.cash_threshold} onChange={changer('cash_threshold')} disabled={!peutEcrire} />
      </Champ>
      <label className="flex items-center justify-between gap-3 rounded-lg border border-border p-3 text-sm sm:col-span-3">
        <span className="flex items-center gap-1">
          Validation des journées par le siège
          <Aide titre="Validation par le siège">Après l’arrêt de la journée par le gérant, le siège valide à son tour.</Aide>
        </span>
        <Switch
          checked={Boolean(form.head_office_validation)}
          onCheckedChange={changer('head_office_validation')}
          disabled={!peutEcrire}
          aria-label="Validation des journées par le siège"
        />
      </label>
      {peutEcrire && (
        <div className="flex items-center justify-between gap-3 sm:col-span-3">
          <span className="text-sm text-muted-foreground tabular-nums">
            {form.cash_threshold !== '' ? `Caisse : ${formatMontant(form.cash_threshold)}` : ''}
          </span>
          <Button type="submit" disabled={enCours}>
            Enregistrer
          </Button>
        </div>
      )}
    </form>
  );
};

// --- Page ----------------------------------------------------------------------------------------

const StationSettingsPage = () => {
  const droits = useDroitsStation();
  const { canSeeMoney } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  // Gérance : montants (droit « montants visibles ») ; registre : module des pompes.
  const onglets = ONGLETS.filter(([cle]) => {
    if (cle === 'gerance') return Boolean(canSeeMoney);
    if (cle === 'registre') return droits.peutLire('pumps');
    return true;
  });
  const onglet = onglets.some(([cle]) => cle === searchParams.get('onglet')) ? searchParams.get('onglet') : 'stations';
  const changerOnglet = (valeur) => {
    const suivants = new URLSearchParams(searchParams);
    suivants.set('onglet', valeur);
    setSearchParams(suivants, { replace: true });
  };

  const { stations, loading: chargementStations, reload: rechargerStations } = useStations();
  const [stationId, setStationId] = useState('');
  const station = stations.find((ligne) => ligne.id === stationId) || null;

  const [produits, setProduits] = useState([]);
  const [cuves, setCuves] = useState([]);
  const [pompes, setPompes] = useState([]);
  const [pistolets, setPistolets] = useState([]);
  const [pompistes, setPompistes] = useState([]);
  const [reglages, setReglages] = useState(null);
  const [chargement, setChargement] = useState(false);
  const [utilisateurs, setUtilisateurs] = useState([]);

  const [dialogue, setDialogue] = useState({ type: null, cible: null, pompeId: null });
  const ouvrir = (type, cible = null, pompeId = null) => setDialogue({ type, cible, pompeId });
  const fermer = () => setDialogue({ type: null, cible: null, pompeId: null });

  const chargerProduits = useCallback(async () => {
    try {
      setProduits((await stationsApi.products.list()) || []);
    } catch (erreur) {
      toast.error(messageErreur(erreur, 'Impossible de charger les produits'));
    }
  }, []);

  const chargerStation = useCallback(
    async (id) => {
      if (!id) return;
      setChargement(true);
      const [lesCuves, lesPompes, lesPistolets, lesPompistes, lesReglages] = await Promise.allSettled([
        droits.peutLire('tanks') ? stationsApi.tanks.list(id) : Promise.resolve([]),
        droits.peutLire('pumps') ? stationsApi.pumps.list(id) : Promise.resolve([]),
        droits.peutLire('pumps') ? stationsApi.nozzles.list(id) : Promise.resolve([]),
        droits.peutLire('attendants') ? stationsApi.attendants.list(id) : Promise.resolve([]),
        droits.peutLire('stations') ? stationsApi.settings.get(id) : Promise.resolve(null),
      ]);
      const valeur = (resultat, repli) => (resultat.status === 'fulfilled' ? resultat.value ?? repli : repli);
      setCuves(valeur(lesCuves, []));
      setPompes(valeur(lesPompes, []));
      setPistolets(valeur(lesPistolets, []));
      setPompistes(valeur(lesPompistes, []));
      setReglages(valeur(lesReglages, null)?.settings || null);
      setChargement(false);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  useEffect(() => {
    chargerProduits();
  }, [chargerProduits]);

  useEffect(() => {
    chargerStation(stationId);
  }, [stationId, chargerStation]);

  useEffect(() => {
    if (!droits.peutChoisirGerant) return;
    fetchAllPages((params) => usersAPI.getAll(params))
      .then(({ items }) => setUtilisateurs(items.filter((utilisateur) => utilisateur.is_active !== false)))
      .catch(() => setUtilisateurs([]));
  }, [droits.peutChoisirGerant]);

  const apresStation = async (enregistree) => {
    await rechargerStations();
    if (enregistree?.id) setStationId(enregistree.id);
  };

  const archiver = async (cible) => {
    if (!window.confirm(`Archiver la station « ${cible.name} » ? Elle sortira des listes.`)) return;
    try {
      await stationsApi.stations.archive(cible.id);
      toast.success('Station archivée');
      if (cible.id === stationId) setStationId('');
      await rechargerStations();
    } catch (erreur) {
      toast.error(messageErreur(erreur, 'Impossible d’archiver la station'));
    }
  };

  const basculerPompiste = async (pompiste, actif) => {
    try {
      await stationsApi.attendants.update(stationId, pompiste.id, { is_active: actif });
      setPompistes((avant) => avant.map((ligne) => (ligne.id === pompiste.id ? { ...ligne, is_active: actif } : ligne)));
    } catch (erreur) {
      toast.error(messageErreur(erreur, 'Impossible de modifier le pompiste'));
    }
  };

  const action = (() => {
    if (onglet === 'stations' && droits.peutEcrire('stations')) {
      return { label: 'Nouvelle station', onClick: () => ouvrir('station') };
    }
    if (onglet === 'produits' && droits.peutEcrire('stations')) {
      return { label: 'Nouveau produit', onClick: () => ouvrir('produit') };
    }
    if (onglet === 'cuves' && station && droits.peutEcrire('tanks')) {
      return { label: 'Nouvelle cuve', onClick: () => ouvrir('cuve') };
    }
    if (onglet === 'pompes' && station && droits.peutEcrire('pumps')) {
      return { label: 'Nouvelle pompe', onClick: () => ouvrir('pompe') };
    }
    if (onglet === 'equipe' && station && droits.peutEcrire('attendants')) {
      return { label: 'Nouveau pompiste', onClick: () => ouvrir('pompiste') };
    }
    return null;
  })();

  const parStation = onglet !== 'stations' && onglet !== 'produits';
  const cuvesBasses = cuves.filter(
    (cuve) => Number(cuve.min_threshold_liters) > 0 && Number(cuve.current_volume_liters) <= Number(cuve.min_threshold_liters),
  ).length;
  const cuvesSansBareme = cuves.filter((cuve) => cuve.is_active && !(cuve.chart_points || []).length).length;

  return (
    <div className="mx-auto w-full max-w-page space-y-4 px-4 py-5 md:px-6 md:py-6 lg:px-8">
      <PageHeader
        eyebrow="Stations"
        title="Réglages"
        description="Stations, produits, cuves et barèmes, pompes et pistolets, équipe et seuils."
        meta={station ? nomLisible(station.name, station.code) : undefined}
        alertes={[
          cuvesBasses > 0 && { niveau: 'critique', texte: `${cuvesBasses} cuve${cuvesBasses > 1 ? 's' : ''} sous le seuil`, court: 'Cuve basse', nombre: cuvesBasses },
          cuvesSansBareme > 0 && {
            niveau: 'attention',
            texte: `${cuvesSansBareme} cuve${cuvesSansBareme > 1 ? 's' : ''} sans barème`,
            court: 'Sans barème',
            nombre: cuvesSansBareme,
            action: { label: 'Voir les cuves', onClick: () => changerOnglet('cuves') },
          },
        ].filter(Boolean)}
        actions={
          <>
            <StationPicker stations={stations} value={stationId} onChange={setStationId} />
            {action && (
              <Button type="button" onClick={action.onClick}>
                <Plus className="h-4 w-4" aria-hidden="true" />
                {action.label}
              </Button>
            )}
          </>
        }
      />

      <Tabs value={onglet} onValueChange={changerOnglet}>
        <TabsList className="h-auto flex-wrap justify-start">
          {onglets.map(([cle, texte]) => (
            <TabsTrigger key={cle} value={cle}>
              {texte}
            </TabsTrigger>
          ))}
        </TabsList>

        {parStation && !station && !chargementStations ? (
          <EmptyState variant="empty" title="Choisissez une station" className="mt-4 border-0" />
        ) : null}

        <TabsContent value="stations">
          <OngletStations
            stations={stations}
            chargement={chargementStations}
            peutEcrire={droits.peutEcrire('stations')}
            onEditer={(cible) => ouvrir('station', cible)}
            onArchiver={archiver}
          />
        </TabsContent>

        <TabsContent value="produits">
          <OngletProduits produits={produits} peutEcrire={droits.peutEcrire('stations')} onEditer={(cible) => ouvrir('produit', cible)} />
        </TabsContent>

        <TabsContent value="cuves">
          {station &&
            (chargement ? (
              <LoadingState shape="readouts" />
            ) : (
              <OngletCuves
                cuves={cuves}
                produits={produits}
                peutEcrire={droits.peutEcrire('tanks')}
                onEditer={(cible) => ouvrir('cuve', cible)}
                onBareme={(cible) => ouvrir('bareme', cible)}
              />
            ))}
        </TabsContent>

        <TabsContent value="pompes">
          {station &&
            (chargement ? (
              <LoadingState shape="table" />
            ) : (
              <OngletPompes
                pompes={pompes}
                pistolets={pistolets}
                cuves={cuves}
                produits={produits}
                peutEcrire={droits.peutEcrire('pumps')}
                onEditerPompe={(cible) => ouvrir('pompe', cible)}
                onAjouterPistolet={(pompe) => ouvrir('pistolet', null, pompe.id)}
                onEditerPistolet={(cible) => ouvrir('pistolet', cible)}
              />
            ))}
        </TabsContent>

        <TabsContent value="equipe">
          {station && (
            <OngletEquipe
              station={station}
              pompistes={pompistes}
              peutEcrireStation={droits.peutEcrire('stations')}
              peutEcrireEquipe={droits.peutEcrire('attendants')}
              onModifierGerant={() => ouvrir('station', station)}
              onBasculer={basculerPompiste}
            />
          )}
        </TabsContent>

        <TabsContent value="gerance">
          {station && <StationManagementSettings station={station} peutEcrire={droits.peutEcrire('stations')} />}
        </TabsContent>

        <TabsContent value="registre">
          {station && <PumpRegistry station={station} peutEcrire={droits.peutEcrire('pumps')} />}
        </TabsContent>

        <TabsContent value="seuils">
          {station && (
            <OngletSeuils
              stationId={station.id}
              reglages={reglages}
              peutEcrire={droits.peutEcrire('stations')}
              onSaved={(suivants) => setReglages(suivants || reglages)}
            />
          )}
        </TabsContent>
      </Tabs>

      <StationDialog
        open={dialogue.type === 'station'}
        onOpenChange={(ouvert) => !ouvert && fermer()}
        station={dialogue.type === 'station' ? dialogue.cible : null}
        utilisateurs={utilisateurs}
        peutChoisirGerant={droits.peutChoisirGerant}
        onSaved={apresStation}
      />
      <ProduitDialog
        open={dialogue.type === 'produit'}
        onOpenChange={(ouvert) => !ouvert && fermer()}
        produit={dialogue.type === 'produit' ? dialogue.cible : null}
        onSaved={chargerProduits}
      />
      {station && (
        <>
          <CuveDialog
            open={dialogue.type === 'cuve'}
            onOpenChange={(ouvert) => !ouvert && fermer()}
            stationId={station.id}
            cuve={dialogue.type === 'cuve' ? dialogue.cible : null}
            produits={produits}
            onSaved={() => chargerStation(station.id)}
          />
          <BaremeDialog
            open={dialogue.type === 'bareme'}
            onOpenChange={(ouvert) => !ouvert && fermer()}
            cuve={dialogue.type === 'bareme' ? dialogue.cible : null}
            onSaved={() => chargerStation(station.id)}
          />
          <PompeDialog
            open={dialogue.type === 'pompe'}
            onOpenChange={(ouvert) => !ouvert && fermer()}
            stationId={station.id}
            pompe={dialogue.type === 'pompe' ? dialogue.cible : null}
            cuves={cuves}
            onSaved={() => chargerStation(station.id)}
          />
          <PistoletDialog
            open={dialogue.type === 'pistolet'}
            onOpenChange={(ouvert) => !ouvert && fermer()}
            stationId={station.id}
            pistolet={dialogue.type === 'pistolet' ? dialogue.cible : null}
            pompeId={dialogue.pompeId}
            pompes={pompes}
            cuves={cuves}
            onSaved={() => chargerStation(station.id)}
          />
          <PompisteDialog
            open={dialogue.type === 'pompiste'}
            onOpenChange={(ouvert) => !ouvert && fermer()}
            stationId={station.id}
            onSaved={() => chargerStation(station.id)}
          />
        </>
      )}
    </div>
  );
};

export default StationSettingsPage;
